import { useMemo, useRef } from "react";
import ModelCollection from "domains/collections/components/ModelCollection";
import { FmSelectionActionsProps } from "domains/file-manager/components/FileManagerV2/FmSelectionActions";
import { FmFileModel } from "domains/file-manager/interfacesV2";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";

import SelectionActionDelete from "./SelectionActionDelete";
import SelectionActionEditTags from "./SelectionActionEditTags";

export type SelectionActionsCustomProps = {
  ignoreActions?: ("editTags" | "editCollections")[];
  isDeleting: boolean;
  onDeleteClick: (files: FmFileModel[]) => void;
};

export type SelectionActionsProps = FmSelectionActionsProps<
  "model",
  SelectionActionsCustomProps
>;

export default function SelectionActions({
  selectedFiles,
  isDisplayed = false,
  ignoreActions = [],
  isDeleting = false,
  onDeleteClick,
}: SelectionActionsProps) {
  const { selectedProject } = useTeamContext();
  const buttonEditTagsRef = useRef<HTMLButtonElement>(null);
  const buttonDeleteRef = useRef<HTMLButtonElement>(null);

  const isLoading = isDeleting;

  const modelsForCollections = useMemo(
    () => selectedFiles.map((item) => item.meta),
    [selectedFiles]
  );

  const isRemovable = useMemo(
    () =>
      !selectedFiles.find((file) => file.meta.ownerId !== selectedProject.id),
    [selectedFiles, selectedProject.id]
  );

  return (
    <>
      <SelectionActionDelete
        selectedFiles={selectedFiles}
        isDisplayed={isDisplayed}
        isDisabled={isLoading || !isRemovable}
        errorMessage={
          !isRemovable
            ? "At least one model in the selection cannot be deleted."
            : undefined
        }
        isLoading={isDeleting}
        buttonRef={buttonDeleteRef}
        onClick={(selectedFiles) => onDeleteClick(selectedFiles)}
      />

      {!ignoreActions.includes("editTags") && (
        <SelectionActionEditTags
          selectedFiles={selectedFiles}
          isDisplayed={isDisplayed}
          buttonRef={buttonEditTagsRef}
        />
      )}

      {!ignoreActions.includes("editCollections") && (
        <ModelCollection models={modelsForCollections} menuPlacement="top" />
      )}
    </>
  );
}
